import { useEffect, useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import ScanResult from './ScanResult';
import { antivirusApis } from '../../../apis/antivirusApis';
import { INSTANCE_PLANS } from '../../../constants';
import { useUser } from '../../../contexts/UserContext';
import useToast, { TOAST_TYPE } from '../../../hooks/useToast';
import { formatDateTimeUTC } from '../../../utils/timeUtils';
import Loading from '../../elements/loading/Loading';
import Text from '../../elements/text/Text';

const VirusScansSection = ({ antivirus, instanceSize }) => {
  const { projectId, instanceId, gpsId } = useParams();
  const { showToast } = useToast();
  const { t } = useTranslation();
  const { user } = useUser();

  const [loading, setLoading] = useState(true);
  const [virusScans, setVirusScans] = useState([]);

  const handleFetchVirusScans = async () => {
    if (antivirus?.enabled) {
      setLoading(true);
      try {
        const { data: virusScanRes } = await antivirusApis.getVirusScanList(
          projectId,
          instanceId || gpsId,
          antivirus.id
        );
        setVirusScans(virusScanRes);
      } catch {
        showToast(
          TOAST_TYPE.ERROR,
          t('virusScanError', { ns: 'notifications' })
        );
        setVirusScans([]);
      }
    } else {
      setVirusScans([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    handleFetchVirusScans();
  }, [antivirus]);

  if (loading) return <Loading />;

  return (
    <>
      {instanceSize === INSTANCE_PLANS.SMALL && (
        <Alert severity="info" variant="filled" icon={<WorkspacePremiumIcon />}>
          {t('antivirus.noAntivirus', { ns: 'instance' })}
        </Alert>
      )}
      <Box>
        {virusScans.map((scan) =>
          scan.clamscanStatus === 'NEVER_RUN' ? (
            <Accordion key={scan.id} disabled>
              <AccordionSummary>
                <Text bodyBold>
                  {t('antivirus.noVirusScan', {
                    ns: 'instance',
                    node: scan.subscriptNumber,
                  })}
                </Text>
              </AccordionSummary>
            </Accordion>
          ) : (
            <Accordion key={scan.id}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Text bodyBold>
                  {t('antivirus.recentScan', {
                    ns: 'instance',
                    node: scan.subscriptNumber,
                    date: formatDateTimeUTC(
                      scan.clamscanResult?.startDate,
                      user?.timeZone
                    ),
                  })}
                </Text>
              </AccordionSummary>
              <AccordionDetails>
                {scan.clamscanStatus === 'ERROR' ? (
                  <Alert severity="error" style={{ width: '100%' }}>
                    {t('antivirus.error', { ns: 'instance' })}
                  </Alert>
                ) : (
                  <ScanResult result={scan} timezone={user?.timeZone} />
                )}
              </AccordionDetails>
            </Accordion>
          )
        )}
      </Box>
    </>
  );
};

VirusScansSection.propTypes = {
  antivirus: PropTypes.object,
  instanceSize: PropTypes.string,
};

export default VirusScansSection;
